<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import EventSidebarSection from './EventSidebarSection.vue';
import EventActivityItem from './EventActivityItem.vue';
import EventsAPI from 'dashboard/api/planning/events';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { formatAuditChangeEntries } from 'dashboard/helper/planningEventAuditHistoryHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { useEmitter } from 'dashboard/composables/emitter';

const PREVIEW_LIMIT = 3;

const props = defineProps({
  eventId: { type: [String, Number], default: null },
  lastChange: { type: Object, default: null },
});

const emit = defineEmits(['viewFull']);
const { t } = useI18n();

const loading = ref(false);
const previewItems = ref([]);
let previewRequestToken = 0;

const fallbackItems = computed(() => {
  const entries = formatAuditChangeEntries(props.lastChange, {
    includeActor: true,
  });
  const createdAt = props.lastChange?.created_at || null;
  return entries.slice(0, PREVIEW_LIMIT).map(entry => ({
    ...entry,
    createdAt,
  }));
});

const items = computed(() =>
  previewItems.value.length ? previewItems.value : fallbackItems.value
);

const flattenHistoryRecords = records => {
  const flattened = [];
  records.forEach(audit => {
    const entries = formatAuditChangeEntries(audit, { includeActor: true });
    entries.forEach(entry => {
      flattened.push({
        ...entry,
        createdAt: audit.created_at,
      });
    });
  });
  return flattened.slice(0, PREVIEW_LIMIT);
};

const fetchPreview = async () => {
  if (!props.eventId || props.eventId === 'undefined') return;

  const requestToken = previewRequestToken + 1;
  previewRequestToken = requestToken;
  const hasItems = items.value.length > 0;
  if (!hasItems) loading.value = true;
  try {
    const response = await EventsAPI.history(props.eventId, { page: 1 });
    if (requestToken !== previewRequestToken) return;
    const rawLogs = response.data.audit_logs || response.data.auditLogs || [];
    const records = Array.isArray(rawLogs) ? rawLogs : Object.values(rawLogs);
    previewItems.value = flattenHistoryRecords(records);
  } catch {
    if (requestToken !== previewRequestToken) return;
    if (!hasItems) previewItems.value = [];
  } finally {
    if (requestToken === previewRequestToken) {
      loading.value = false;
    }
  }
};

const onPlanningEventUpdated = data => {
  if (!data?.id || !props.eventId) return;
  if (String(data.id) !== String(props.eventId)) return;
  const incomingChangeAt = data.last_change?.created_at;
  if (
    incomingChangeAt &&
    incomingChangeAt === props.lastChange?.created_at &&
    previewItems.value.length
  ) {
    return;
  }
  fetchPreview();
};

useEmitter(BUS_EVENTS.PLANNING_EVENT_UPDATED, onPlanningEventUpdated);

onMounted(fetchPreview);

watch(
  () => props.eventId,
  (next, prev) => {
    if (next && next !== prev) fetchPreview();
  }
);

watch(
  () => props.lastChange?.created_at,
  (next, prev) => {
    if (next && next !== prev) fetchPreview();
  }
);
</script>

<template>
  <div class="print:hidden">
    <EventSidebarSection
      :title="t('PLANNING.SHOW.ACTIVITY_TITLE')"
      icon="i-lucide-history"
    >
      <div v-if="loading && !items.length" class="flex justify-center py-4">
        <Spinner />
      </div>
      <div v-else-if="items.length" class="relative">
        <div
          class="absolute left-4 top-4 bottom-4 w-px bg-n-slate-5"
          aria-hidden="true"
        />
        <ul class="mb-0 list-none space-y-4 pl-0">
          <li
            v-for="(item, index) in items"
            :key="`${item.field}-${item.createdAt}-${index}`"
            class="relative"
          >
            <EventActivityItem :item="item" show-actor>
              <p
                v-if="item.createdAt"
                class="mb-0 mt-1 text-xs text-n-slate-10"
              >
                {{ dynamicTime(item.createdAt) }}
              </p>
            </EventActivityItem>
          </li>
        </ul>
      </div>
      <p v-else class="mb-0 text-sm text-n-slate-11">
        {{ t('PLANNING.SHOW.CHANGELOG_EMPTY') }}
      </p>

      <Button
        variant="ghost"
        size="sm"
        color="slate"
        class="mt-3 !px-0"
        :label="t('PLANNING.SHOW.CHANGELOG_VIEW_FULL')"
        icon="i-lucide-history"
        @click="emit('viewFull')"
      />
    </EventSidebarSection>
  </div>
</template>
